import WelcomeScreen from "./components/menus/WelcomeScreen";
import HUDManager from './hud/HUDManager';

export default class UIManager {
    constructor() {
        this.hudManager = new HUDManager();
        this.fuelBar = null;
        this.fuelText = null;
        this.outOfFuelWarning = null;
        this.fuelFlashInterval = null;
        this.notificationTimeout = null;
        this.speedDisplay = null;
        this.speedDisplayComponent = null;
        this.coinsDisplay = null;
        this.welcomeScreen = null;
        this.loadingIndicator = null;
        this.infoButton = null;
        this.attributionPopup = null;
        this.eventFeedContainer = null;
        this.eventSlots = {};
        this.actionPrompt = null;
        this.damageVignette = null;
        this.meteorCountdown = null;
        this.settingsPanel = null;
        this.settingsVisible = false;
        this.performanceButton = null;
        this.performanceModeEnabled = false;
        this.nightModeButton = null;
        this.nightModeEnabled = false;
        this.performanceToggleHandler = null;
        this.nightModeToggleHandler = null;
        this.cameraDistanceHandler = null;
        this.cameraFovHandler = null;
        this.controllerDeadzoneHandler = null;
        this.controllerSensitivityHandler = null;
        this.vibrationIntensityHandler = null;
        this.fpsOverlayToggleHandler = null;
        this.accessibilityPresetHandler = null;
        this.controllerBadge = null;
        this.controllerStatusHandler = null;
        this.controllerDisconnectNotice = null;
        this.controllerDisconnectTimer = null;
        this.gameOverGamepadRafId = null;
        this.gameOverPrevCrossPressed = false;
        this.gameOverPrevOptionsPressed = false;
        this.performanceOverlay = null;
        this.performanceOverlayVisible = false;
        this.accessibilityPreset = 'default';
        
        // Laser meter elements
        this.laserBar = null;
        this.laserText = null;
        this.laserRechargeInterval = null;
        this.laserCharge = 100; // Starting laser charge (%)
        this.laserRechargeRate = 10; // % per second
        this.laserIsRecharging = false;
        
        // Controls info tracking
        this.controlsInfoTimeout = null;
    }

    // Initialize UI elements
    initialize() {
        this.hudManager.initialize();
        // Find existing UI elements
        this.fuelBar = document.getElementById('fuel-level');
        this.fuelText = document.getElementById('fuel-percentage');
        this.speedDisplay = document.getElementById('speed');
        this.coinsDisplay = document.getElementById('coins-value');

        // Create info button
        this.createInfoButton();
        
        // Create laser meter (delegated to HUDManager)
        this.createLaserMeter(); // Now safe, only HUDManager manages the DOM

        // Show welcome screen
        this.showWelcomeScreen();

        // Create loading indicator
        this.createLoadingIndicator();
        this.createEventFeed();
        this.createActionPrompt();
        this.createDamageVignette();
        this.createMeteorCountdown();
        this.createSettingsPanel();
        this.createControllerBadge();
        this.setupControllerStatusListener();
        this.createScoreHUD();
        this.createChallengeHUD();
    }

    // HUD methods delegated to HUDManager
    updateFuel(value) {
        this.hudManager.updateFuel(value);
    }
    updateCoins(value) {
        this.hudManager.updateCoins(value);
    }
    updateSpeed(value) {
        this.hudManager.updateSpeed(value);
    }
    // TODO: Delegate laser meter and other HUD methods to HUDManager

    // Delegate HUD creation methods to HUDManager
    createFuelDisplay() {
        return this.hudManager.createFuelDisplay();
    }
    createLaserMeter() {
        return this.hudManager.createLaserMeter();
    } // Delegated to HUDManager; legacy DOM logic removed
    updateLaserMeter(percentage = null) {
        return this.hudManager.updateLaserMeter(percentage);
    } // Delegated to HUDManager; legacy DOM logic removed

    // Deprecated: legacy direct HUD methods (to be removed)
    // Create speed display
    createSpeedDisplay() {
        if (!this.speedDisplayComponent) {
            console.log("Creating speed display with UIManager");
            
            // Import SpeedDisplay component
            import('./components/hud/SpeedDisplay.js').then(module => {
                const SpeedDisplay = module.default;
                this.speedDisplayComponent = new SpeedDisplay({
                    position: {
                        top: window.innerWidth < 768 ? '10px' : '20px',
                        left: window.innerWidth < 768 ? '10px' : '20px'
                    }
                });
                this.speedDisplayComponent.initialize();
            });
        }

        return this.speedDisplayComponent;
    }

    // Create coins display
    createCoinsDisplay() {
        // Create coins display if it doesn't exist
        if (!this.coinsDisplay) {
            console.log("Creating coins display with UIManager");

            // Create container
            const coinsContainer = document.createElement('div');
            coinsContainer.id = 'coins-container';
            coinsContainer.style.position = 'fixed';
            coinsContainer.style.top = window.innerWidth < 768 ? '60px' : '80px';
            coinsContainer.style.left = window.innerWidth < 768 ? '10px' : '20px';
            coinsContainer.style.backgroundColor = 'rgba(20, 16, 10, 0.55)';
            coinsContainer.style.color = '#E8B84B';
            coinsContainer.style.padding = window.innerWidth < 768 ? '8px 12px' : '8px 12px';
            coinsContainer.style.borderRadius = '6px';
            coinsContainer.style.fontFamily = 'Arial, sans-serif';
            coinsContainer.style.fontSize = window.innerWidth < 768 ? '14px' : '18px';
            coinsContainer.style.zIndex = '100';

            // Create label
            const coinsLabel = document.createElement('span');
            coinsLabel.textContent = 'Coins: ';

            // Create value
            const coinsValue = document.createElement('span');
            coinsValue.id = 'coins-value';
            coinsValue.style.fontWeight = 'bold';
            coinsValue.textContent = '0';

            // Assemble container
            coinsContainer.appendChild(coinsLabel);
            coinsContainer.appendChild(coinsValue);
            document.body.appendChild(coinsContainer);

            // Store reference
            this.coinsDisplay = coinsValue;
        }

        return this.coinsDisplay;
    }

    // Update fuel display based on the bus's fuel level
    updateFuelDisplay(event) {
        if (!this.fuelBar || !this.fuelText) {
            console.warn("Fuel display elements not found in UIManager");
            return;
        }

        // Handle both event-based and direct updates
        let level, capacity, percentage, empty;

        if (event && event.detail) {
            // Event-based update
            ({ level, capacity, percentage, empty } = event.detail);
        } else if (event && typeof event === 'object') {
            // Direct object parameter
            ({ level, capacity, percentage, empty } = event);
        } else {
            console.warn("Invalid parameters for updateFuelDisplay in UIManager");
            return;
        }

        // Update fuel percentage text
        this.fuelText.textContent = `${Math.round(percentage)}%`;

        // Update fuel bar height for vertical gauge
        this.fuelBar.style.height = `${percentage}%`;

        // Change fuel bar color based on fuel level
        if (percentage <= 10) {
            // Critical - muted red and flashing
            this.fuelBar.style.backgroundColor = '#D45B4A';

            // Add flashing effect when critically low
            if (!this.fuelFlashInterval && percentage <= 5) {
                this.fuelFlashInterval = setInterval(() => {
                    this.fuelBar.style.opacity = this.fuelBar.style.opacity === '1' ? '0.3' : '1';
                }, 500);
            }
        } else if (percentage <= 30) {
            // Low - muted orange
            this.fuelBar.style.backgroundColor = '#D4924B';

            // Clear flash interval if it exists
            if (this.fuelFlashInterval) {
                clearInterval(this.fuelFlashInterval);
                this.fuelFlashInterval = null;
                this.fuelBar.style.opacity = '1';
            }
        } else {
            // Good - muted green
            this.fuelBar.style.backgroundColor = '#7BC67E';

            // Clear flash interval if it exists
            if (this.fuelFlashInterval) {
                clearInterval(this.fuelFlashInterval);
                this.fuelFlashInterval = null;
                this.fuelBar.style.opacity = '1';
            }
        }

        // Show warning if out of fuel
        if (empty && !this.outOfFuelWarning) {
            this.showOutOfFuelWarning();
        }

        // Dismiss game-over overlay if fuel was restored (e.g. refueled at a gas station)
        if (!empty && this.outOfFuelWarning) {
            this.dismissOutOfFuelWarning();
        }
    }

    dismissOutOfFuelWarning() {
        if (!this.outOfFuelWarning) return;
        this.stopGameOverGamepadPolling();
        this.outOfFuelWarning.style.opacity = '0';
        // Remove from DOM after fade-out
        setTimeout(() => {
            if (this.outOfFuelWarning && this.outOfFuelWarning.parentNode) {
                this.outOfFuelWarning.parentNode.removeChild(this.outOfFuelWarning);
            }
            this.outOfFuelWarning = null;
        }, 500);
    }

    // Show warning when out of fuel
    showOutOfFuelWarning() {
        if (this.outOfFuelWarning) return;

        // Create warning container with fade-in animation
        this.outOfFuelWarning = document.createElement('div');
        this.outOfFuelWarning.style.position = 'fixed';
        this.outOfFuelWarning.style.top = '50%';
        this.outOfFuelWarning.style.left = '50%';
        this.outOfFuelWarning.style.transform = 'translate(-50%, -50%)';
        this.outOfFuelWarning.style.padding = '30px';
        this.outOfFuelWarning.style.backgroundColor = 'rgba(20, 16, 10, 0.92)';
        this.outOfFuelWarning.style.color = '#F5E6D0';
        this.outOfFuelWarning.style.fontFamily = 'Arial, sans-serif';
        this.outOfFuelWarning.style.textAlign = 'center';
        this.outOfFuelWarning.style.borderRadius = '6px';
        this.outOfFuelWarning.style.zIndex = '1000';
        this.outOfFuelWarning.style.opacity = '0';
        this.outOfFuelWarning.style.transition = 'opacity 0.5s ease-in-out';

        // Game Over text with style
        const gameOverText = document.createElement('div');
        gameOverText.textContent = 'GAME OVER';
        gameOverText.style.fontSize = '36px';
        gameOverText.style.fontWeight = 'bold';
        gameOverText.style.color = '#D45B4A';
        gameOverText.style.marginBottom = '10px';

        // Out of fuel message
        const fuelMessage = document.createElement('div');
        fuelMessage.textContent = 'Your bus ran out of fuel!';
        fuelMessage.style.fontSize = '18px';
        fuelMessage.style.marginBottom = '20px';
        fuelMessage.style.color = '#D4924B';

        // Score summary
        const scoreContainer = document.createElement('div');
        scoreContainer.style.backgroundColor = 'rgba(245, 230, 208, 0.06)';
        scoreContainer.style.padding = '15px';
        scoreContainer.style.borderRadius = '6px';
        scoreContainer.style.marginBottom = '25px';

        const coinsCollected = document.getElementById('coins-value')?.textContent || '0';
        const finalScore = window.game?.scoreManager?.score || 0;
        const highScore = window.game?.scoreManager?.highScore || 0;
        const challengesCompleted = window.game?.scoreManager?.totalChallengesCompleted || 0;
        scoreContainer.innerHTML = `
            <div style="font-size: 16px; margin-bottom: 10px;">FINAL SCORE</div>
            <div style="font-size: 28px; color: #E8B84B; margin-bottom:6px;">${finalScore.toLocaleString()}</div>
            <div style="font-size: 14px; color: rgba(245,230,208,0.65); margin-bottom:4px;">HIGH SCORE: ${highScore.toLocaleString()}</div>
            <div style="font-size: 14px; color: #E8B84B;">🪙 ${coinsCollected} coins &nbsp; 🏁 ${challengesCompleted} challenges</div>
        `;

        // Create restart button with enhanced style
        const restartButton = document.createElement('button');
        restartButton.textContent = 'Play Again';
        restartButton.style.padding = '15px 30px';
        restartButton.style.fontSize = '20px';
        restartButton.style.backgroundColor = '#5BB8A6';
        restartButton.style.color = '#F5E6D0';
        restartButton.style.border = 'none';
        restartButton.style.borderRadius = '6px';
        restartButton.style.cursor = 'pointer';
        restartButton.style.transition = 'background-color 0.2s ease';

        restartButton.onmouseover = () => {
            restartButton.style.backgroundColor = '#4FA898';
        };
        restartButton.onmouseout = () => {
            restartButton.style.backgroundColor = '#5BB8A6';
        };

        // Add click handler
        restartButton.onclick = () => {
            this.restartGame();
        };

        const controllerHint = document.createElement('div');
        controllerHint.textContent = 'Controller: Press X or Options to restart';
        controllerHint.style.fontSize = '13px';
        controllerHint.style.marginTop = '12px';
        controllerHint.style.opacity = '0.8';

        // Assemble all elements
        this.outOfFuelWarning.appendChild(gameOverText);
        this.outOfFuelWarning.appendChild(fuelMessage);
        this.outOfFuelWarning.appendChild(scoreContainer);
        this.outOfFuelWarning.appendChild(restartButton);
        this.outOfFuelWarning.appendChild(controllerHint);

        // Add to DOM and trigger fade-in
        document.body.appendChild(this.outOfFuelWarning);
        requestAnimationFrame(() => {
            this.outOfFuelWarning.style.opacity = '1';
        });

        this.startGameOverGamepadPolling();
    }

    // Show a notification message
    showNotificationMessage(message, backgroundColor = "rgba(0, 100, 180, 0.8)") {
        const category = this.inferNotificationCategory(message, backgroundColor);
        this.pushEventFeedMessage(category, message, backgroundColor);
    }

    // Show refueling notification
    showRefuelingNotification(stationName = '', refuelCost = 10) {
        const message = stationName ?
            `Refueling at ${stationName} - Cost: $${refuelCost}` :
            `Refueling - Cost: $${refuelCost}`;

        this.showNotificationMessage(message, "rgba(0, 150, 0, 0.8)");
    }

    restartGame() {
        this.stopGameOverGamepadPolling();
        window.location.reload();
    }

    startGameOverGamepadPolling() {
        if (this.gameOverGamepadRafId) return;

        const poll = () => {
            const gamepads = navigator.getGamepads ? navigator.getGamepads() : [];
            const active = gamepads ? Array.from(gamepads).find((gp) => gp && gp.connected) : null;

            if (active) {
                const crossPressed = !!active.buttons?.[0]?.pressed;    // PS5 X
                const optionsPressed = !!active.buttons?.[9]?.pressed;  // PS5 Options

                if ((crossPressed && !this.gameOverPrevCrossPressed) ||
                    (optionsPressed && !this.gameOverPrevOptionsPressed)) {
                    this.restartGame();
                    return;
                }

                this.gameOverPrevCrossPressed = crossPressed;
                this.gameOverPrevOptionsPressed = optionsPressed;
            } else {
                this.gameOverPrevCrossPressed = false;
                this.gameOverPrevOptionsPressed = false;
            }

            this.gameOverGamepadRafId = requestAnimationFrame(poll);
        };

        this.gameOverGamepadRafId = requestAnimationFrame(poll);
    }

    stopGameOverGamepadPolling() {
        if (this.gameOverGamepadRafId) {
            cancelAnimationFrame(this.gameOverGamepadRafId);
            this.gameOverGamepadRafId = null;
        }
        this.gameOverPrevCrossPressed = false;
        this.gameOverPrevOptionsPressed = false;
    }

    updateSpeedDisplay(speed) {
        if (!this.speedDisplayComponent) {
            console.warn("Speed display component not found in UIManager");
            return;
        }

        // Update speed display using the component, pass speed directly in km/h
        this.speedDisplayComponent.update(speed * 3.6, 'km/h');
    }


    // Update coins display
    updateCoinsDisplay(coins) {
        const coinsElement = document.getElementById('coins-value');
        if (coinsElement) {
            coinsElement.textContent = coins;
        }
    }

    // Show camera mode indicator
    showCameraModeIndicator(mode) {
        // Get or create camera mode indicator
        let indicator = document.getElementById('camera-mode');
        if (!indicator) {
            indicator = document.createElement('div');
            indicator.id = 'camera-mode';
            indicator.style.position = 'fixed';
            indicator.style.top = '165px';
            indicator.style.right = '30px';
            indicator.style.padding = '8px 12px';
            indicator.style.backgroundColor = 'rgba(20, 16, 10, 0.55)';
            indicator.style.color = '#F5E6D0';
            indicator.style.fontFamily = 'Arial, sans-serif';
            indicator.style.fontSize = '14px';
            indicator.style.borderRadius = '6px';
            indicator.style.zIndex = '1000';
            indicator.style.transition = 'opacity 0.5s, transform 0.3s';
            document.body.appendChild(indicator);
        }

        // Set indicator text and opacity
        indicator.textContent = `Camera: ${mode}`;
        indicator.style.opacity = '1';
        indicator.style.transform = 'translateY(0)';

        // Hide indicator after 2 seconds
        setTimeout(() => {
            indicator.style.opacity = '0';
            indicator.style.transform = 'translateY(-20px)';
        }, 2000);
    }

    // Show controls information
    // Create loading indicator
    createLoadingIndicator() {
        if (!this.loadingIndicator) {
            this.loadingIndicator = document.createElement('div');
            this.loadingIndicator.id = 'loading-indicator';
            this.loadingIndicator.style.position = 'fixed';
            this.loadingIndicator.style.top = '50%';
            this.loadingIndicator.style.left = '50%';
            this.loadingIndicator.style.transform = 'translate(-50%, -50%)';
            this.loadingIndicator.style.backgroundColor = 'rgba(20, 16, 10, 0.85)';
            this.loadingIndicator.style.color = '#F5E6D0';
            this.loadingIndicator.style.padding = '20px 40px';
            this.loadingIndicator.style.borderRadius = '6px';
            this.loadingIndicator.style.fontFamily = 'Arial, sans-serif';
            this.loadingIndicator.style.fontSize = '18px';
            this.loadingIndicator.style.zIndex = '2000';
            this.loadingIndicator.style.display = 'none';
            this.loadingIndicator.innerHTML = 'Loading Bus Model...<br><div class="loading-spinner"></div>';

            // Add CSS for the spinner
            const style = document.createElement('style');
            style.textContent = `
                .loading-spinner {
                    width: 40px;
                    height: 40px;
                    margin: 20px auto 0;
                    border: 4px solid #f3f3f3;
                    border-top: 4px solid #3498db;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `;
            document.head.appendChild(style);
            document.body.appendChild(this.loadingIndicator);
        }
        return this.loadingIndicator;
    }

    // Show/hide loading indicator
    showLoadingIndicator(show = true) {
        if (this.loadingIndicator) {
            this.loadingIndicator.style.display = show ? 'block' : 'none';
        }
    }

    showControlsInfo() {
        // First, create or get the controls panel
        let controlsContainer = document.getElementById('controls-info');
        
        // If it already exists, just make it visible again
        if (controlsContainer) {
            // Stop any existing hiding animation
            controlsContainer.style.opacity = '1';
            controlsContainer.style.transform = 'translateY(0)';
            
            // Clear any existing timeout
            if (this.controlsInfoTimeout) {
                clearTimeout(this.controlsInfoTimeout);
            }
        } else {
            // Create the panel if it doesn't exist
            controlsContainer = document.createElement('div');
            controlsContainer.id = 'controls-info';
            controlsContainer.style.position = 'fixed';
            controlsContainer.style.bottom = '50px';
            controlsContainer.style.left = '20px';
            controlsContainer.style.backgroundColor = 'rgba(20, 16, 10, 0.65)';
            controlsContainer.style.color = '#F5E6D0';
            controlsContainer.style.padding = '15px';
            controlsContainer.style.borderRadius = '6px';
            controlsContainer.style.fontFamily = 'Arial, sans-serif';
            controlsContainer.style.fontSize = '14px';
            controlsContainer.style.zIndex = '1000';
            controlsContainer.style.maxWidth = '300px';
            controlsContainer.style.transition = 'opacity 0.5s, transform 0.5s';
            
            // Create controls list
            const controlsList = document.createElement('ul');
            controlsList.style.listStyleType = 'none';
            controlsList.style.padding = '0';
            controlsList.style.margin = '0';

            // Add controls info
            const controls = [
                { key: 'W/A/S/D', description: 'Drive + steer' },
                { key: 'Space', description: 'Fire laser' },
                { key: 'R', description: 'Reload' },
                { key: 'V', description: 'Camera mode' },
                { key: 'F4', description: 'Quick settings' },
                { key: 'F6', description: 'FPS overlay' },
                { key: 'N', description: 'Night mode' }
            ];
            const controllerControls = [
                { key: 'L Stick', description: 'Steer / fallback drive' },
                { key: 'R2 / L2', description: 'Throttle / brake' },
                { key: 'R1', description: 'Fire laser' },
                { key: 'Square', description: 'Reload' },
                { key: 'Triangle', description: 'Camera mode' },
                { key: 'X / Options', description: 'Start + restart' }
            ];

            // Create title
            const title = document.createElement('div');
            title.textContent = 'CONTROLS';
            title.style.fontWeight = 'bold';
            title.style.marginBottom = '10px';
            title.style.textAlign = 'center';
            title.style.borderBottom = '1px solid rgba(245, 230, 208, 0.15)';
            title.style.paddingBottom = '5px';

            controlsContainer.appendChild(title);

            // Add each control to the list
            controls.forEach(control => {
                const item = document.createElement('li');
                item.style.marginBottom = '5px';
                item.style.display = 'flex';
                item.style.justifyContent = 'space-between';

                const keySpan = document.createElement('span');
                keySpan.textContent = control.key;
                keySpan.style.fontWeight = 'bold';
                keySpan.style.backgroundColor = 'rgba(245, 230, 208, 0.12)';
                keySpan.style.padding = '2px 6px';
                keySpan.style.borderRadius = '4px';
                keySpan.style.marginRight = '10px';
                keySpan.style.minWidth = '40px';
                keySpan.style.textAlign = 'center';

                const descSpan = document.createElement('span');
                descSpan.textContent = control.description;
                descSpan.style.flexGrow = '1';

                item.appendChild(keySpan);
                item.appendChild(descSpan);
                controlsList.appendChild(item);
            });

            controlsContainer.appendChild(controlsList);

            const padTitle = document.createElement('div');
            padTitle.textContent = 'PS5 CONTROLLER';
            padTitle.style.fontWeight = 'bold';
            padTitle.style.margin = '10px 0 6px';
            padTitle.style.borderTop = '1px solid rgba(245, 230, 208, 0.15)';
            padTitle.style.paddingTop = '8px';
            controlsContainer.appendChild(padTitle);

            const padList = document.createElement('ul');
            padList.style.listStyleType = 'none';
            padList.style.padding = '0';
            padList.style.margin = '0';
            controllerControls.forEach((control) => {
                const item = document.createElement('li');
                item.style.marginBottom = '4px';
                item.style.display = 'flex';
                item.style.justifyContent = 'space-between';

                const keySpan = document.createElement('span');
                keySpan.textContent = control.key;
                keySpan.style.fontWeight = 'bold';
                keySpan.style.backgroundColor = 'rgba(245, 230, 208, 0.12)';
                keySpan.style.padding = '2px 6px';
                keySpan.style.borderRadius = '4px';
                keySpan.style.marginRight = '10px';

                const descSpan = document.createElement('span');
                descSpan.textContent = control.description;
                descSpan.style.flexGrow = '1';

                item.appendChild(keySpan);
                item.appendChild(descSpan);
                padList.appendChild(item);
            });
            controlsContainer.appendChild(padList);

            // Add auto-hide message
            const autoHideMsg = document.createElement('div');
            autoHideMsg.textContent = 'Press TAB to toggle controls';
            autoHideMsg.style.display = 'block';
            autoHideMsg.style.margin = '10px auto 0';
            autoHideMsg.style.padding = '5px 10px';
            autoHideMsg.style.color = 'rgba(255, 255, 255, 0.7)';
            autoHideMsg.style.fontSize = '12px';
            autoHideMsg.style.textAlign = 'center';

            controlsContainer.appendChild(autoHideMsg);

            // Add to DOM
            document.body.appendChild(controlsContainer);
        }
        
        // Set timeout to fade out the controls
        this.controlsInfoTimeout = setTimeout(() => {
            if (controlsContainer) {
                // Fade out but keep in DOM
                controlsContainer.style.opacity = '0';
                controlsContainer.style.transform = 'translateY(20px)';
            }
            this.controlsInfoTimeout = null;
        }, 3400);
    }

    hideControlsInfo() {
        const controlsContainer = document.getElementById('controls-info');
        if (controlsContainer) {
            // Fade out
            controlsContainer.style.opacity = '0';
            controlsContainer.style.transform = 'translateY(20px)';
            
            // Clear timeout if exists
            if (this.controlsInfoTimeout) {
                clearTimeout(this.controlsInfoTimeout);
                this.controlsInfoTimeout = null;
            }
        }
    }

    toggleControlsInfo() {
        const controlsContainer = document.getElementById('controls-info');
        if (controlsContainer && controlsContainer.style.opacity !== '0') {
            this.hideControlsInfo();
        } else {
            this.showControlsInfo();
        }
    }

    // Show welcome screen
    showWelcomeScreen() {
        if (!this.welcomeScreen) {
            this.welcomeScreen = new WelcomeScreen();
        }
        this.welcomeScreen.show();
    }

    // Create info button and attribution popup
    createInfoButton() {
        if (!this.infoButton) {
            // Create info button
            this.infoButton = document.createElement('button');
            this.infoButton.id = 'info-button';
            this.infoButton.style.position = 'fixed';
            this.infoButton.style.top = window.innerWidth < 768 ? '10px' : '250px';
            this.infoButton.style.right = window.innerWidth < 768 ? '10px' : '45px';
            this.infoButton.style.width = '30px';
            this.infoButton.style.height = '30px';
            this.infoButton.style.backgroundColor = 'rgba(20, 16, 10, 0.55)';
            this.infoButton.style.color = '#F5E6D0';
            this.infoButton.style.border = 'none';
            //this.infoButton.style.borderRadius = '50%';
            this.infoButton.style.cursor = 'pointer';
            this.infoButton.style.fontSize = '16px';
            this.infoButton.style.display = window.innerWidth < 768 ? 'none' : 'flex';
            this.infoButton.style.alignItems = 'center';
            this.infoButton.style.justifyContent = 'center';
            this.infoButton.innerHTML = 'ⓘ';

            // Create attribution popup
            this.attributionPopup = document.createElement('div');
            this.attributionPopup.id = 'attribution-popup';
            this.attributionPopup.style.position = 'fixed';
            this.attributionPopup.style.top = '245px';
            this.attributionPopup.style.right = '80px';
            //sthis.attributionPopup.style.transform = 'translate(-50%, -50%)';
            this.attributionPopup.style.backgroundColor = 'rgba(20, 16, 10, 0.85)';
            this.attributionPopup.style.color = '#F5E6D0';
            this.attributionPopup.style.padding = '10px';
            this.attributionPopup.style.margin = "0px";
            this.attributionPopup.style.borderRadius = '6px';
            this.attributionPopup.style.zIndex = '2000';
            this.attributionPopup.style.display = 'none';
            this.attributionPopup.style.maxWidth = '400px';
            this.attributionPopup.style.textAlign = 'center';
            this.attributionPopup.style.textAlign = 'left';
            this.attributionPopup.style.fontSize = '14px';
            const attribution = document.createElement('p');
            attribution.style.margin = '0';
            attribution.innerHTML = '<span">💎 Click TAB for Controls</span><p style="margin: 5px 0px">🚌 SCHOOL BUS by Kyle Li <a href="https://creativecommons.org/licenses/by/3.0/" target="_blank" style="color: #3498db;">[CC-BY]</a> via <a href="https://poly.pizza/m/2_1dZHNPJqJ" target="_blank" style="color: #3498db;">Poly Pizza</a></p>';

            this.attributionPopup.appendChild(attribution);

            // Add event listeners
            this.infoButton.addEventListener('click', () => {
                this.attributionPopup.style.display = 'block';
                setTimeout(() => {
                    this.attributionPopup.style.display = 'none';
                }, 3000);
            });

            // Add to DOM
            document.body.appendChild(this.infoButton);
            document.body.appendChild(this.attributionPopup);
        }
    }

    // Check if laser is available (delegated to HUDManager)
    isLaserAvailable() {
        return this.hudManager.isLaserAvailable();
    }

    // Deplete laser (delegated to HUDManager)
    depleteLaser(amount = 25) {
        return this.hudManager.depleteLaser(amount);
    }

    // Start laser recharge (delegated to HUDManager)
    startLaserRecharge() {
        return this.hudManager.startLaserRecharge();
    }

    // Stop laser recharge (delegated to HUDManager)
    stopLaserRecharge() {
        return this.hudManager.stopLaserRecharge();
    }

    createControllerBadge() {
        if (this.controllerBadge) return;
        const badge = document.createElement('div');
        badge.id = 'controller-badge';
        badge.style.position = 'fixed';
        badge.style.top = '165px';
        badge.style.right = '30px';
        badge.style.padding = '7px 10px';
        badge.style.borderRadius = '6px';
        badge.style.backgroundColor = 'rgba(20, 16, 10, 0.55)';
        badge.style.border = 'none';
        badge.style.color = '#F5E6D0';
        badge.style.fontFamily = 'Arial, sans-serif';
        badge.style.fontSize = '12px';
        badge.style.fontWeight = 'bold';
        badge.style.letterSpacing = '0.3px';
        badge.style.zIndex = '1300';
        badge.style.display = 'none';
        badge.textContent = '🎮 PS5';
        document.body.appendChild(badge);
        this.controllerBadge = badge;
    }

    setupControllerStatusListener() {
        if (this.controllerStatusHandler) return;
        this.controllerStatusHandler = (event) => {
            const connected = !!event?.detail?.connected;
            const type = event?.detail?.type || 'generic';
            this.updateControllerBadge(connected, type);
            if (connected) {
                this.hideControllerDisconnectedNotice();
            } else {
                this.showControllerDisconnectedNotice();
            }
        };
        window.addEventListener('controller_status_changed', this.controllerStatusHandler);
    }

    updateControllerBadge(connected, type = 'generic') {
        if (!this.controllerBadge) this.createControllerBadge();
        if (!connected) {
            this.controllerBadge.style.display = 'none';
            return;
        }
        this.controllerBadge.textContent = type === 'dualsense' ? '🎮 PS5' : '🎮 Controller';
        this.controllerBadge.style.display = 'block';
    }

    showControllerDisconnectedNotice() {
        if (!this.controllerDisconnectNotice) {
            const notice = document.createElement('div');
            notice.id = 'controller-disconnect-notice';
            notice.style.position = 'fixed';
            notice.style.top = '54px';
            notice.style.right = '20px';
            notice.style.padding = '8px 10px';
            notice.style.borderRadius = '6px';
            notice.style.backgroundColor = 'rgba(212, 91, 74, 0.65)';
            notice.style.border = 'none';
            notice.style.color = '#F5E6D0';
            notice.style.fontFamily = 'Arial, sans-serif';
            notice.style.fontSize = '12px';
            notice.style.zIndex = '1300';
            notice.style.maxWidth = '230px';
            notice.textContent = 'Controller disconnected. Keyboard fallback: W/A/S/D + Space';
            this.controllerDisconnectNotice = notice;
            document.body.appendChild(notice);
        }
        this.controllerDisconnectNotice.style.display = 'block';

        if (this.controllerDisconnectTimer) {
            clearTimeout(this.controllerDisconnectTimer);
        }
        this.controllerDisconnectTimer = setTimeout(() => {
            this.hideControllerDisconnectedNotice();
        }, 5500);
    }

    hideControllerDisconnectedNotice() {
        if (!this.controllerDisconnectNotice) return;
        this.controllerDisconnectNotice.style.display = 'none';
        if (this.controllerDisconnectTimer) {
            clearTimeout(this.controllerDisconnectTimer);
            this.controllerDisconnectTimer = null;
        }
    }

    createEventFeed() {
        if (this.eventFeedContainer) return;
        const container = document.createElement('div');
        container.id = 'event-feed';
        container.style.position = 'fixed';
        container.style.top = '76px';
        container.style.left = '50%';
        container.style.transform = 'translateX(-50%)';
        container.style.display = 'flex';
        container.style.flexDirection = 'column';
        container.style.gap = '8px';
        container.style.zIndex = '1200';
        container.style.pointerEvents = 'none';

        const makeSlot = (id, label) => {
            const slot = document.createElement('div');
            slot.id = id;
            slot.style.minWidth = '240px';
            slot.style.maxWidth = '420px';
            slot.style.padding = '8px 14px';
            slot.style.borderRadius = '6px';
            slot.style.opacity = '0';
            slot.style.color = '#F5E6D0';
            slot.style.fontSize = '14px';
            slot.style.fontFamily = 'Arial, sans-serif';
            slot.style.transition = 'opacity 0.2s ease';
            slot.style.backgroundColor = 'rgba(20, 16, 10, 0.55)';
            slot.textContent = label;
            return slot;
        };

        this.eventSlots.hazard = makeSlot('event-hazard', '');
        this.eventSlots.economy = makeSlot('event-economy', '');
        container.appendChild(this.eventSlots.hazard);
        container.appendChild(this.eventSlots.economy);
        document.body.appendChild(container);
        this.eventFeedContainer = container;
    }

    inferNotificationCategory(message, backgroundColor = '') {
        const text = `${message} ${backgroundColor}`.toLowerCase();
        if (text.includes('meteor') || text.includes('impact') || text.includes('warning')) {
            return 'hazard';
        }
        return 'economy';
    }

    pushEventFeedMessage(category, message, backgroundColor) {
        this.createEventFeed();
        const slot = this.eventSlots[category] || this.eventSlots.economy;
        slot.textContent = message;
        slot.style.backgroundColor = backgroundColor;
        slot.style.opacity = '1';

        if (!slot._hideTimer) {
            slot._hideTimer = null;
        }
        clearTimeout(slot._hideTimer);
        slot._hideTimer = setTimeout(() => {
            slot.style.opacity = '0';
        }, 2600);
    }

    createActionPrompt() {
        if (this.actionPrompt) return;
        const prompt = document.createElement('div');
        prompt.id = 'action-prompt';
        prompt.style.position = 'fixed';
        prompt.style.left = '50%';
        prompt.style.bottom = window.innerWidth < 768 ? '26%' : '18%';
        prompt.style.transform = 'translateX(-50%)';
        prompt.style.padding = '8px 14px';
        prompt.style.borderRadius = '6px';
        prompt.style.backgroundColor = 'rgba(20, 16, 10, 0.55)';
        prompt.style.color = '#F5E6D0';
        prompt.style.fontFamily = 'Arial, sans-serif';
        prompt.style.fontSize = '14px';
        prompt.style.opacity = '0';
        prompt.style.transition = 'opacity 0.2s ease';
        prompt.style.pointerEvents = 'none';
        prompt.style.zIndex = '1100';
        document.body.appendChild(prompt);
        this.actionPrompt = prompt;
    }

    setActionPrompt(text = '', tone = 'neutral') {
        this.createActionPrompt();
        if (!text) {
            this.actionPrompt.style.opacity = '0';
            return;
        }
        this.actionPrompt.textContent = text;
        this.actionPrompt.style.backgroundColor = tone === 'success'
            ? 'rgba(123, 198, 126, 0.5)'
            : tone === 'warning'
                ? 'rgba(212, 146, 75, 0.55)'
                : 'rgba(20, 16, 10, 0.55)';
        this.actionPrompt.style.opacity = '1';
    }

    createDamageVignette() {
        if (this.damageVignette) return;
        const vignette = document.createElement('div');
        vignette.style.position = 'fixed';
        vignette.style.inset = '0';
        vignette.style.pointerEvents = 'none';
        vignette.style.zIndex = '1090';
        vignette.style.opacity = '0';
        vignette.style.transition = 'opacity 140ms ease-out';
        vignette.style.background = 'radial-gradient(circle, rgba(255,0,0,0) 55%, rgba(255,30,30,0.5) 100%)';
        document.body.appendChild(vignette);
        this.damageVignette = vignette;
    }

    showDamageVignette(intensity = 0.8) {
        this.createDamageVignette();
        const clamped = Math.max(0.15, Math.min(1, intensity));
        this.damageVignette.style.opacity = `${clamped}`;
        setTimeout(() => {
            if (this.damageVignette) {
                this.damageVignette.style.opacity = '0';
            }
        }, 180);
    }

    pulseFuelGauge() {
        if (!this.fuelBar) return;
        this.fuelBar.style.transition = 'transform 120ms ease-out, height 0.3s, background-color 0.3s';
        this.fuelBar.style.transform = 'scaleX(1.12)';
        setTimeout(() => {
            if (this.fuelBar) this.fuelBar.style.transform = 'scaleX(1)';
        }, 140);
    }

    createMeteorCountdown() {
        if (this.meteorCountdown) return;

        // Container — full-screen center overlay, no background
        const el = document.createElement('div');
        el.id = 'meteor-countdown';
        el.style.cssText = `
            position:fixed; top:200px; left:200px;
            z-index:1250; pointer-events:none;
            opacity:0; transition:opacity 0.15s ease;
            display:flex; flex-direction:column; align-items:center; gap:0;
        `;

        // Big number
        const bigNum = document.createElement('div');
        bigNum.id = 'meteor-countdown-num';
        bigNum.style.cssText = `
            font-family:Arial,sans-serif;
            font-size:120px; font-weight:900; line-height:1;
            color:#D45B4A;
        `;

        // Label underneath
        const label = document.createElement('div');
        label.id = 'meteor-countdown-label';
        label.style.cssText = `
            font-family:Arial,sans-serif; font-size:18px; font-weight:bold;
            color:#D45B4A; text-transform:uppercase;
        `;
        label.textContent = 'METEOR INCOMING';

        // Count badge
        const badge = document.createElement('div');
        badge.id = 'meteor-countdown-badge';
        badge.style.cssText = `
            font-family:Arial,sans-serif; font-size:14px;
            color:rgba(212, 91, 74, 0.7); margin-top:4px;
        `;

        el.appendChild(bigNum);
        el.appendChild(label);
        el.appendChild(badge);
        document.body.appendChild(el);
        this.meteorCountdown = el;
        this.meteorCountdownNum = bigNum;
        this.meteorCountdownLabel = label;
        this.meteorCountdownBadge = badge;
    }

    showMeteorCountdown(seconds, count = 0) {
        this.createMeteorCountdown();
        this.updateMeteorCountdown(seconds, count);
    }

    updateMeteorCountdown(seconds, count = 0) {
        this.createMeteorCountdown();

        // Show whole seconds as big number, decimal as smaller suffix
        const whole = Math.ceil(seconds);
        this.meteorCountdownNum.textContent = whole;

        // Bigger on last second
        if (whole <= 1) {
            this.meteorCountdownNum.style.fontSize = '140px';
        } else {
            this.meteorCountdownNum.style.fontSize = '120px';
        }

        this.meteorCountdownBadge.textContent = `${count} meteor${count !== 1 ? 's' : ''} incoming`;
        this.meteorCountdown.style.opacity = '1';
    }

    clearMeteorCountdown() {
        if (!this.meteorCountdown) return;
        this.meteorCountdown.style.opacity = '0';
    }

    createPerformanceOverlay() {
        if (this.performanceOverlay) return;
        const overlay = document.createElement('div');
        overlay.id = 'perf-mini-overlay';
        overlay.style.position = 'fixed';
        overlay.style.left = '12px';
        overlay.style.top = '12px';
        overlay.style.padding = '7px 9px';
        overlay.style.borderRadius = '6px';
        overlay.style.fontFamily = 'monospace';
        overlay.style.fontSize = '11px';
        overlay.style.color = '#F5E6D0';
        overlay.style.backgroundColor = 'rgba(20, 16, 10, 0.65)';
        overlay.style.zIndex = '1300';
        overlay.style.display = 'none';
        overlay.innerHTML = '<div>FPS: 0</div><div>FT: 0.0 ms</div>';
        document.body.appendChild(overlay);
        this.performanceOverlay = overlay;
    }

    togglePerformanceOverlay(forceVisible = null) {
        this.createPerformanceOverlay();
        this.performanceOverlayVisible = forceVisible === null ? !this.performanceOverlayVisible : !!forceVisible;
        this.performanceOverlay.style.display = this.performanceOverlayVisible ? 'block' : 'none';
        return this.performanceOverlayVisible;
    }

    updatePerformanceOverlay(fps, frameTime) {
        if (!this.performanceOverlayVisible || !this.performanceOverlay) return;
        this.performanceOverlay.innerHTML = `<div>FPS: ${fps}</div><div>FT: ${frameTime.toFixed(1)} ms</div>`;
    }

    applyAccessibilityPreset(preset = 'default') {
        this.accessibilityPreset = preset;
        const warningPalette = {
            default: { bg: 'rgba(120,0,0,0.7)', border: '1px solid rgba(255,140,140,0.7)' },
            highContrast: { bg: 'rgba(0,36,88,0.82)', border: '1px solid rgba(0,214,255,0.9)' },
            deuteranopia: { bg: 'rgba(0,42,96,0.78)', border: '1px solid rgba(255,210,90,0.85)' }
        };
        const selected = warningPalette[preset] || warningPalette.default;
        if (this.meteorCountdown) {
            // Keep container transparent — only the text elements are visible
            this.meteorCountdown.style.backgroundColor = 'transparent';
            this.meteorCountdown.style.border = 'none';
        }
    }

    createSettingsPanel() {
        if (this.settingsPanel) return;
        const panel = document.createElement('div');
        panel.id = 'ui-settings-panel';
        panel.style.position = 'fixed';
        panel.style.right = '20px';
        panel.style.top = '58%';
        panel.style.transform = 'translateY(-50%)';
        panel.style.width = '220px';
        panel.style.padding = '10px';
        panel.style.borderRadius = '6px';
        panel.style.backgroundColor = 'rgba(20, 16, 10, 0.65)';
        panel.style.color = '#F5E6D0';
        panel.style.fontFamily = 'Arial, sans-serif';
        panel.style.fontSize = '12px';
        panel.style.zIndex = '1100';
        panel.style.display = 'none';

        const title = document.createElement('div');
        title.textContent = 'Quick Settings';
        title.style.fontWeight = 'bold';
        title.style.marginBottom = '8px';
        panel.appendChild(title);

        const perfBtn = document.createElement('button');
        perfBtn.textContent = 'Performance: OFF';
        perfBtn.style.width = '100%';
        perfBtn.style.marginBottom = '8px';
        perfBtn.style.padding = '6px';
        perfBtn.style.borderRadius = '6px';
        perfBtn.style.border = '1px solid rgba(245, 230, 208, 0.15)';
        perfBtn.style.backgroundColor = 'rgba(245, 230, 208, 0.08)';
        perfBtn.style.color = '#F5E6D0';
        perfBtn.style.cursor = 'pointer';
        perfBtn.onclick = () => {
            this.performanceModeEnabled = !this.performanceModeEnabled;
            perfBtn.textContent = `Performance: ${this.performanceModeEnabled ? 'ON' : 'OFF'}`;
            if (typeof this.performanceToggleHandler === 'function') {
                this.performanceToggleHandler(this.performanceModeEnabled);
            }
        };
        panel.appendChild(perfBtn);
        this.performanceButton = perfBtn;

        const nightBtn = document.createElement('button');
        nightBtn.textContent = 'Night Mode: OFF';
        nightBtn.style.width = '100%';
        nightBtn.style.marginBottom = '8px';
        nightBtn.style.padding = '6px';
        nightBtn.style.borderRadius = '6px';
        nightBtn.style.border = '1px solid rgba(245, 230, 208, 0.15)';
        nightBtn.style.backgroundColor = 'rgba(245, 230, 208, 0.08)';
        nightBtn.style.color = '#F5E6D0';
        nightBtn.style.cursor = 'pointer';
        nightBtn.onclick = () => {
            this.nightModeEnabled = !this.nightModeEnabled;
            nightBtn.textContent = `Night Mode: ${this.nightModeEnabled ? 'ON' : 'OFF'}`;
            if (typeof this.nightModeToggleHandler === 'function') {
                this.nightModeToggleHandler(this.nightModeEnabled);
            }
        };
        panel.appendChild(nightBtn);
        this.nightModeButton = nightBtn;

        const fpsBtn = document.createElement('button');
        fpsBtn.textContent = 'FPS Overlay: OFF';
        fpsBtn.style.width = '100%';
        fpsBtn.style.marginBottom = '8px';
        fpsBtn.style.padding = '6px';
        fpsBtn.style.borderRadius = '6px';
        fpsBtn.style.border = '1px solid rgba(245, 230, 208, 0.15)';
        fpsBtn.style.backgroundColor = 'rgba(245, 230, 208, 0.08)';
        fpsBtn.style.color = '#F5E6D0';
        fpsBtn.style.cursor = 'pointer';
        fpsBtn.onclick = () => {
            const visible = this.togglePerformanceOverlay();
            fpsBtn.textContent = `FPS Overlay: ${visible ? 'ON' : 'OFF'}`;
            if (typeof this.fpsOverlayToggleHandler === 'function') {
                this.fpsOverlayToggleHandler(visible);
            }
        };
        panel.appendChild(fpsBtn);
        this.fpsOverlayButton = fpsBtn;

        const makeSlider = (labelText, min, max, step, initial, onInput) => {
            const wrap = document.createElement('div');
            wrap.style.marginBottom = '8px';
            const label = document.createElement('div');
            label.textContent = labelText;
            label.style.marginBottom = '3px';
            const input = document.createElement('input');
            input.type = 'range';
            input.min = String(min);
            input.max = String(max);
            input.step = String(step);
            input.value = String(initial);
            input.style.width = '100%';
            input.oninput = () => onInput(Number(input.value));
            wrap.appendChild(label);
            wrap.appendChild(input);
            panel.appendChild(wrap);
            return input;
        };

        this.cameraDistanceSlider = makeSlider('Camera Distance', 10, 60, 1, 25, (value) => {
            if (typeof this.cameraDistanceHandler === 'function') this.cameraDistanceHandler(value);
        });
        this.cameraFovSlider = makeSlider('Camera FOV', 45, 95, 1, 70, (value) => {
            if (typeof this.cameraFovHandler === 'function') this.cameraFovHandler(value);
        });
        this.controllerDeadzoneSlider = makeSlider('Controller Deadzone', 0, 0.35, 0.01, 0.1, (value) => {
            if (typeof this.controllerDeadzoneHandler === 'function') this.controllerDeadzoneHandler(value);
        });
        this.controllerSensitivitySlider = makeSlider('Steering Sensitivity', 0.6, 1.8, 0.05, 1, (value) => {
            if (typeof this.controllerSensitivityHandler === 'function') this.controllerSensitivityHandler(value);
        });
        this.vibrationSlider = makeSlider('Vibration Intensity', 0, 1, 0.05, 0.75, (value) => {
            if (typeof this.vibrationIntensityHandler === 'function') this.vibrationIntensityHandler(value);
        });

        const accessWrap = document.createElement('div');
        accessWrap.style.marginBottom = '8px';
        const accessLabel = document.createElement('div');
        accessLabel.textContent = 'Alert Colors';
        accessLabel.style.marginBottom = '3px';
        const accessSelect = document.createElement('select');
        accessSelect.style.width = '100%';
        accessSelect.style.padding = '4px';
        accessSelect.style.borderRadius = '4px';
        accessSelect.style.background = 'rgba(245, 230, 208, 0.08)';
        accessSelect.style.color = '#F5E6D0';
        accessSelect.style.border = '1px solid rgba(245, 230, 208, 0.15)';
        [
            { value: 'default', label: 'Default' },
            { value: 'highContrast', label: 'High Contrast' },
            { value: 'deuteranopia', label: 'Deuteranopia' }
        ].forEach((opt) => {
            const option = document.createElement('option');
            option.value = opt.value;
            option.textContent = opt.label;
            option.style.color = '#111';
            accessSelect.appendChild(option);
        });
        accessSelect.onchange = () => {
            this.applyAccessibilityPreset(accessSelect.value);
            if (typeof this.accessibilityPresetHandler === 'function') {
                this.accessibilityPresetHandler(accessSelect.value);
            }
        };
        accessWrap.appendChild(accessLabel);
        accessWrap.appendChild(accessSelect);
        panel.appendChild(accessWrap);
        this.accessibilitySelect = accessSelect;

        document.body.appendChild(panel);
        this.settingsPanel = panel;
    }

    toggleSettingsPanel(forceVisible = null) {
        if (!this.settingsPanel) return false;
        if (window.innerWidth < 768) return false;

        this.settingsVisible = forceVisible === null ? !this.settingsVisible : !!forceVisible;
        this.settingsPanel.style.display = this.settingsVisible ? 'block' : 'none';
        return this.settingsVisible;
    }

    setSettingsHandlers({
        onTogglePerformance,
        onToggleNightMode,
        onCameraDistance,
        onCameraFov,
        onControllerDeadzone,
        onControllerSensitivity,
        onVibrationIntensity,
        onToggleFpsOverlay,
        onAccessibilityPreset,
        performanceEnabled = false,
        nightModeEnabled = false,
        cameraDistance = 25,
        cameraFov = 70,
        controllerDeadzone = 0.1,
        controllerSensitivity = 1,
        vibrationIntensity = 0.75,
        fpsOverlayEnabled = false,
        accessibilityPreset = 'default'
    } = {}) {
        this.performanceToggleHandler = onTogglePerformance || null;
        this.nightModeToggleHandler = onToggleNightMode || null;
        this.cameraDistanceHandler = onCameraDistance || null;
        this.cameraFovHandler = onCameraFov || null;
        this.controllerDeadzoneHandler = onControllerDeadzone || null;
        this.controllerSensitivityHandler = onControllerSensitivity || null;
        this.vibrationIntensityHandler = onVibrationIntensity || null;
        this.fpsOverlayToggleHandler = onToggleFpsOverlay || null;
        this.accessibilityPresetHandler = onAccessibilityPreset || null;
        this.performanceModeEnabled = !!performanceEnabled;
        this.nightModeEnabled = !!nightModeEnabled;

        if (this.performanceButton) {
            this.performanceButton.textContent = `Performance: ${this.performanceModeEnabled ? 'ON' : 'OFF'}`;
        }
        if (this.nightModeButton) {
            this.nightModeButton.textContent = `Night Mode: ${this.nightModeEnabled ? 'ON' : 'OFF'}`;
        }
        if (this.fpsOverlayButton) {
            this.fpsOverlayButton.textContent = `FPS Overlay: ${fpsOverlayEnabled ? 'ON' : 'OFF'}`;
        }
        this.togglePerformanceOverlay(!!fpsOverlayEnabled);
        if (this.cameraDistanceSlider) {
            this.cameraDistanceSlider.value = String(Math.round(cameraDistance));
        }
        if (this.cameraFovSlider) {
            this.cameraFovSlider.value = String(Math.round(cameraFov));
        }
        if (this.controllerDeadzoneSlider) {
            this.controllerDeadzoneSlider.value = String(controllerDeadzone);
        }
        if (this.controllerSensitivitySlider) {
            this.controllerSensitivitySlider.value = String(controllerSensitivity);
        }
        if (this.vibrationSlider) {
            this.vibrationSlider.value = String(vibrationIntensity);
        }
        if (this.accessibilitySelect) {
            this.accessibilitySelect.value = accessibilityPreset;
        }
        this.applyAccessibilityPreset(accessibilityPreset);
    }

    setNightModeState(enabled) {
        this.nightModeEnabled = !!enabled;
        if (this.nightModeButton) {
            this.nightModeButton.textContent = `Night Mode: ${this.nightModeEnabled ? 'ON' : 'OFF'}`;
        }
    }

    // ── Score & Challenge HUD ──────────────────────────────────────

    createScoreHUD() {
        if (this.scoreContainer) return;

        // Score display (top-center)
        this.scoreContainer = document.createElement('div');
        this.scoreContainer.id = 'score-hud';
        this.scoreContainer.style.cssText = `
            position:fixed; top:38px; left:50%; transform:translateX(-50%);
            display:flex; align-items:center; gap:12px;
            font-family:Arial,sans-serif; z-index:1100; pointer-events:none;
        `;

        // Score value
        this.scoreValue = document.createElement('div');
        this.scoreValue.style.cssText = `
            background:rgba(20, 16, 10, 0.55); padding:6px 14px; border-radius:6px;
            color:#F5E6D0; font-size:15px; font-weight:bold;
        `;
        this.scoreValue.textContent = 'SCORE: 0';
        this.scoreContainer.appendChild(this.scoreValue);

        // Multiplier badge
        this.multiplierBadge = document.createElement('div');
        this.multiplierBadge.style.cssText = `
            background:rgba(232, 184, 75, 0.75); padding:4px 10px; border-radius:6px;
            color:#F5E6D0; font-size:13px; font-weight:bold;
            display:none;
        `;
        this.multiplierBadge.textContent = 'x1';
        this.scoreContainer.appendChild(this.multiplierBadge);

        // Combo badge
        this.comboBadge = document.createElement('div');
        this.comboBadge.style.cssText = `
            background:rgba(196, 91, 138, 0.75); padding:4px 10px; border-radius:6px;
            color:#F5E6D0; font-size:13px; font-weight:bold;
            display:none;
        `;
        this.comboBadge.textContent = 'COMBO x0';
        this.scoreContainer.appendChild(this.comboBadge);

        document.body.appendChild(this.scoreContainer);
    }

    updateScoreDisplay(score, points, type) {
        if (!this.scoreContainer) this.createScoreHUD();
        this.scoreValue.textContent = `SCORE: ${score.toLocaleString()}`;

        // Flash the points earned
        if (points > 0) {
            this.showScorePopup(points, type);
        }
    }

    showScorePopup(points, type) {
        const popup = document.createElement('div');
        popup.style.cssText = `
            position:fixed; top:70px; left:50%; transform:translateX(-50%);
            color:#E8B84B; font-family:Arial,sans-serif; font-size:14px;
            font-weight:bold; z-index:1101; pointer-events:none;
            transition:all 0.6s ease-out; opacity:1;
        `;
        popup.textContent = `+${points.toLocaleString()}`;
        document.body.appendChild(popup);

        requestAnimationFrame(() => {
            popup.style.top = '56px';
            popup.style.opacity = '0';
        });

        setTimeout(() => {
            if (popup.parentNode) popup.parentNode.removeChild(popup);
        }, 700);
    }

    updateMultiplierDisplay(multiplier) {
        if (!this.scoreContainer) this.createScoreHUD();
        if (multiplier > 1) {
            this.multiplierBadge.textContent = `x${multiplier}`;
            this.multiplierBadge.style.display = 'block';
        } else {
            this.multiplierBadge.style.display = 'none';
        }
    }

    updateComboDisplay(combo) {
        if (!this.scoreContainer) this.createScoreHUD();
        if (combo > 1) {
            this.comboBadge.textContent = `COMBO x${combo}`;
            this.comboBadge.style.display = 'block';
        } else {
            this.comboBadge.style.display = 'none';
        }
    }

    // ── Challenge HUD ──────────────────────────────────────────────

    createChallengeHUD() {
        if (this.challengeHUD) return;

        this.challengeHUD = document.createElement('div');
        this.challengeHUD.id = 'challenge-hud';
        this.challengeHUD.style.cssText = `
            position:fixed; bottom:20px; left:50%; transform:translateX(-50%);
            background:rgba(20, 16, 10, 0.6); padding:10px 20px; border-radius:6px;
            color:#F5E6D0; font-family:Arial,sans-serif; font-size:14px;
            z-index:1100;
            min-width:280px; text-align:center; display:none;
            pointer-events:none;
        `;

        // Challenge title row
        this.challengeTitle = document.createElement('div');
        this.challengeTitle.style.cssText = `
            font-weight:bold; font-size:15px; margin-bottom:6px;
            color:#5BB8A6;
        `;
        this.challengeHUD.appendChild(this.challengeTitle);

        // Progress bar container
        const barContainer = document.createElement('div');
        barContainer.style.cssText = `
            width:100%; height:8px; background:rgba(255,255,255,0.15);
            border-radius:4px; overflow:hidden; margin-bottom:6px;
        `;
        this.challengeProgressBar = document.createElement('div');
        this.challengeProgressBar.style.cssText = `
            height:100%; background:linear-gradient(90deg,#5BB8A6,#7BC67E);
            border-radius:4px; transition:width 0.2s ease; width:0%;
        `;
        barContainer.appendChild(this.challengeProgressBar);
        this.challengeHUD.appendChild(barContainer);

        // Status row (progress text + timer)
        this.challengeStatus = document.createElement('div');
        this.challengeStatus.style.cssText = `
            display:flex; justify-content:space-between; font-size:13px;
            color:rgba(255,255,255,0.8);
        `;
        this.challengeProgressText = document.createElement('span');
        this.challengeTimerText = document.createElement('span');
        this.challengeTimerText.style.color = '#E8B84B';
        this.challengeStatus.appendChild(this.challengeProgressText);
        this.challengeStatus.appendChild(this.challengeTimerText);
        this.challengeHUD.appendChild(this.challengeStatus);

        document.body.appendChild(this.challengeHUD);
    }

    updateChallengeHUD(status) {
        if (!status) {
            if (this.challengeHUD) this.challengeHUD.style.display = 'none';
            return;
        }

        if (!this.challengeHUD) this.createChallengeHUD();
        this.challengeHUD.style.display = 'block';

        this.challengeTitle.textContent = `${status.icon} ${status.name}`;
        this.challengeProgressText.textContent = status.progressText || '';

        // Timer with color coding
        const timeRemaining = status.timeRemaining;
        this.challengeTimerText.textContent = `${timeRemaining.toFixed(1)}s`;
        if (timeRemaining < 5) {
            this.challengeTimerText.style.color = '#D45B4A';
        } else if (timeRemaining < 10) {
            this.challengeTimerText.style.color = '#D4924B';
        } else {
            this.challengeTimerText.style.color = '#E8B84B';
        }

        // Progress bar
        const progress = status.total > 0 ? (status.current / status.total) * 100 : 0;
        this.challengeProgressBar.style.width = `${Math.min(100, progress)}%`;
    }

    showChallengeNotification(challenge, eventType, reason = '') {
        const colors = {
            start: { bg: 'rgba(20, 16, 10, 0.8)', border: 'rgba(91, 184, 166, 0.5)', text: '#5BB8A6' },
            complete: { bg: 'rgba(20, 16, 10, 0.8)', border: 'rgba(123, 198, 126, 0.5)', text: '#7BC67E' },
            fail: { bg: 'rgba(20, 16, 10, 0.8)', border: 'rgba(212, 91, 74, 0.5)', text: '#D45B4A' },
        };
        const c = colors[eventType] || colors.start;

        const messages = {
            start: `${challenge.icon} ${challenge.name} — GO!`,
            complete: `${challenge.icon} ${challenge.name} COMPLETE! +${challenge.reward}`,
            fail: `${challenge.icon} ${challenge.name} FAILED${reason ? ': ' + reason : ''}`,
        };

        const el = document.createElement('div');
        el.style.cssText = `
            position:fixed; top:50%; left:50%; transform:translate(-50%,-50%);
            background:${c.bg}; border:2px solid ${c.border};
            padding:16px 32px; border-radius:6px;
            color:${c.text}; font-family:Arial,sans-serif;
            font-size:22px; font-weight:bold; text-align:center;
            z-index:1300; opacity:0; transition:all 0.4s ease;
            pointer-events:none;
        `;
        el.textContent = messages[eventType];
        document.body.appendChild(el);

        requestAnimationFrame(() => { el.style.opacity = '1'; });

        setTimeout(() => {
            el.style.opacity = '0';
            el.style.transform = 'translate(-50%,-50%) scale(1.1)';
            setTimeout(() => { if (el.parentNode) el.parentNode.removeChild(el); }, 400);
        }, eventType === 'start' ? 2000 : 2500);

        // Hide challenge HUD on complete/fail
        if (eventType !== 'start' && this.challengeHUD) {
            setTimeout(() => { this.challengeHUD.style.display = 'none'; }, 300);
        }
    }
}
